import { Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Reveal, CountUp } from "@project/user/components/reveal";
import { Button } from "@project/components/ui/button";
import {
  HandHeart,
  Stethoscope,
  UtensilsCrossed,
  Sparkles,
  ShieldCheck,
  Heart,
  Quote,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { useI18n } from "@project/lib/use-i18n";
import careImg from "@project/assets/home-care.webp";
import mealImg from "@project/assets/home-meal.webp";
import medicalImg from "@project/assets/home-medical.webp";

const services = [
  {
    icon: Heart,
    titleKey: "care.elderCare.services.shelter.title",
    bodyKey: "care.elderCare.services.shelter.body",
  },
  {
    icon: UtensilsCrossed,
    titleKey: "care.elderCare.services.bhojan.title",
    bodyKey: "care.elderCare.services.bhojan.body",
  },
  {
    icon: Stethoscope,
    titleKey: "care.elderCare.services.care.title",
    bodyKey: "care.elderCare.services.care.body",
  },
  {
    icon: Sparkles,
    titleKey: "care.elderCare.services.dignity.title",
    bodyKey: "care.elderCare.services.dignity.body",
  },
];

const facilities = [
  "care.elderCare.facilities.items.capacity",
  "care.elderCare.facilities.items.clinic",
  "care.elderCare.facilities.items.physio",
  "care.elderCare.facilities.items.library",
  "care.elderCare.facilities.items.access",
  "care.elderCare.facilities.items.garden",
];

const stories = [
  {
    nameKey: "care.elderCare.stories.items.kantilal.name",
    yearsKey: "care.elderCare.stories.items.kantilal.years",
    quoteKey: "care.elderCare.stories.items.kantilal.quote",
  },
  {
    nameKey: "care.elderCare.stories.items.saroj.name",
    yearsKey: "care.elderCare.stories.items.saroj.years",
    quoteKey: "care.elderCare.stories.items.saroj.quote",
  },
  {
    nameKey: "care.elderCare.stories.items.hasmukh.name",
    yearsKey: "care.elderCare.stories.items.hasmukh.years",
    quoteKey: "care.elderCare.stories.items.hasmukh.quote",
  },
];

export default function ElderCareProgramPage() {
  const { t } = useI18n();
  return (
    <PageShell>
      <PageHero
        eyebrow={t("care.elderCare.hero.eyebrow")}
        title={t("care.elderCare.hero.title")}
        subtitle={t("care.elderCare.hero.subtitle")}
        image={careImg}
      />

      {/* Impact strip */}
      <section className="border-border/60 bg-card/40 border-b">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              n: 120,
              suffix: "+",
              label: t("care.elderCare.impact.residentsCared"),
            },
            {
              n: 25,
              suffix: "+ Yrs",
              label: t("care.elderCare.impact.lifetimeCare"),
            },
            {
              n: 360,
              suffix: "K+",
              label: t("care.elderCare.impact.mealsServed"),
            },
            {
              n: 100,
              suffix: "%",
              label: t("care.elderCare.impact.donorFunded"),
            },
          ].map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-background p-5">
                <div className="bg-primary/10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-primary">
                  <HandHeart className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-serif text-3xl font-semibold text-foreground">
                    <CountUp to={s.n} suffix={s.suffix} />
                  </div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Care services */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <Reveal className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            {t("care.elderCare.overview.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground md:text-4xl">
            {t("care.elderCare.overview.heading")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            {t("care.elderCare.overview.description")}
          </p>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => (
            <Reveal key={s.titleKey} variant="zoom" delay={i * 100}>
              <article className="card-lift h-full rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="bg-primary/10 grid h-12 w-12 place-items-center rounded-xl text-primary">
                  <s.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-foreground">
                  {t(s.titleKey)}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t(s.bodyKey)}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Facilities */}
      <section className="bg-secondary/40">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:grid-cols-2 md:items-center">
          <Reveal variant="zoom">
            <figure className="overflow-hidden rounded-3xl border border-border shadow-sm">
              <img
                src={mealImg}
                alt={t("care.elderCare.facilities.imgAlt")}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </figure>
          </Reveal>
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
              {t("care.elderCare.facilities.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">
              {t("care.elderCare.facilities.heading")}
            </h2>
            <p className="mt-3 text-muted-foreground">
              {t("care.elderCare.facilities.description")}
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {facilities.map(f => (
                <li
                  key={f}
                  className="text-foreground/80 flex items-start gap-2 text-sm"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{" "}
                  {t(f)}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Impact stories */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <Reveal className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            {t("care.elderCare.stories.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">
            {t("care.elderCare.stories.heading")}
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {stories.map((s, i) => (
            <Reveal key={s.nameKey} delay={i * 110}>
              <figure className="card-lift h-full rounded-3xl border border-border bg-card p-7 shadow-sm">
                <Quote className="h-6 w-6 text-gold" />
                <blockquote className="mt-3 font-serif text-lg italic leading-snug text-foreground">
                  {t(s.quoteKey)}
                </blockquote>
                <figcaption className="mt-6 border-t border-border pt-4">
                  <div className="text-sm font-semibold text-foreground">
                    {t(s.nameKey)}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t(s.yearsKey)}
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Photo gallery */}
      <section className="bg-card/40 border-border/60 border-y">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <Reveal className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
              {t("care.elderCare.gallery.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">
              {t("care.elderCare.gallery.heading")}
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { src: careImg, label: t("care.elderCare.gallery.items.care") },
              { src: mealImg, label: t("care.elderCare.gallery.items.meals") },
              {
                src: medicalImg,
                label: t("care.elderCare.gallery.items.health"),
              },
            ].map((g, i) => (
              <Reveal key={g.label} variant="zoom" delay={i * 110}>
                <figure className="card-lift group relative overflow-hidden rounded-2xl border border-border shadow-sm">
                  <img
                    src={g.src}
                    alt={g.label}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="from-primary/85 via-primary/20 pointer-events-none absolute inset-0 bg-gradient-to-t to-transparent" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-4 text-primary-foreground">
                    <div className="text-base font-bold">{g.label}</div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button
              asChild
              variant="outline"
              className="border-primary/30 rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
            >
              <Link to="/gallery">
                {t("care.elderCare.gallery.viewFull")}{" "}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center">
          <Reveal>
            <ShieldCheck className="mx-auto h-7 w-7 text-gold" />
            <h2 className="mt-4 font-serif text-3xl font-semibold md:text-4xl">
              {t("care.elderCare.cta.heading")}
            </h2>
            <p className="text-primary-foreground/80 mx-auto mt-3 max-w-xl">
              {t("care.elderCare.cta.description")}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button
                asChild
                size="lg"
                className="hover:bg-gold/90 rounded-full bg-gold text-gold-foreground"
              >
                <Link to="/donate">
                  <HandHeart className="mr-2 h-4 w-4" />{" "}
                  {t("care.elderCare.cta.support")}
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-primary-foreground/40 hover:bg-primary-foreground/10 rounded-full bg-transparent text-primary-foreground hover:text-primary-foreground"
              >
                <Link to="/contact">{t("care.elderCare.cta.visit")}</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
